import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const ContactTripModal = ({ show, onHide, tripName, onSubmit }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.email || !formData.message) {
      setError(t('planDetailShare.contactModal.fillAllFields'));
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('planDetailShare.contactModal.validEmail'));
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      await onSubmit(formData);
      setSuccess(true);
      // Clear form
      setFormData({ email: '', message: '' });
      // Keep modal open so user can see success message and close manually
    } catch (err) {
      setError(err.message || t('planDetailShare.contactModal.failedToSend'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ email: '', message: '' });
      setError('');
      setSuccess(false);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-envelope me-2"></i>
          {t('planDetailShare.contactTripOwner')}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {success ? (
          <Alert variant="success">
            <i className="bi bi-check-circle me-2"></i>
            {t('planDetailShare.contactModal.successMessage')}
          </Alert>
        ) : (
          <>
            <p className="text-muted mb-3">
              {t('planDetailShare.contactModal.sendMessageAbout', { tripName: tripName || '' })}
            </p>
            
            {error && (
              <Alert variant="danger" className="mb-3">
                <i className="bi bi-exclamation-circle me-2"></i>
                {error}
              </Alert>
            )}
            
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetailShare.contactModal.yourEmail')} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('planDetailShare.contactModal.emailPlaceholder')}
                  disabled={isSubmitting}
                  required
                />
                <Form.Text className="text-muted">
                  {t('planDetailShare.contactModal.emailHelp')}
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-4">
                <Form.Label>{t('planDetailShare.contactModal.message')} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t('planDetailShare.contactModal.messagePlaceholder')}
                  disabled={isSubmitting}
                  required
                />
                <Form.Text className="text-muted">
                  {t('planDetailShare.contactModal.messageHelp')}
                </Form.Text>
              </Form.Group>
            </Form>
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        {success ? (
          <Button variant="primary" onClick={handleClose}>
            {t('planDetailShare.contactModal.close')}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              {t('planDetailShare.contactModal.cancel')}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.email || !formData.message}
            >
              {isSubmitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  {t('planDetailShare.contactModal.sending')}
                </>
              ) : (
                <>
                  <i className="bi bi-send me-2"></i>
                  {t('planDetailShare.contactModal.sendMessage')}
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ContactTripModal;
